import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PageHeader from '../components/common/PageHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import { schwachstellenService } from '../services/schwachstellenService';
import { ReviewQueueItem } from '../types';
import { formatSeverityLower, getKritikalitaetMuiColor } from '../utils/severity';

const COLUMN_COUNT = 8;

const isOwnReview = (item: ReviewQueueItem, userId: number | undefined): boolean =>
  userId != null && item.reporter?.id === userId;

const formatWartezeit = (reviewStartAt?: string): string => {
  if (!reviewStartAt) return '–';
  const diffMs = Date.now() - new Date(reviewStartAt).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `vor ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours}h`;
  const days = Math.floor(hours / 24);
  return `vor ${days}d`;
};

interface ReviewQueueRowProps {
  item: ReviewQueueItem;
  selectable: boolean;
  accentColor: string;
  rowBg?: string;
  ownAccent?: string;
  claiming: boolean;
  onClaim: (id: number) => void;
}

const ReviewQueueRow: React.FC<ReviewQueueRowProps> = ({
  item,
  selectable,
  accentColor,
  rowBg,
  ownAccent,
  claiming,
  onClaim,
}) => {
  const reviewStartAt = item.review_requested_at ?? item.erstellt_am;
  const wartezeit = formatWartezeit(reviewStartAt);
  const wartezeitMs = reviewStartAt ? Date.now() - new Date(reviewStartAt).getTime() : 0;
  const isUrgent = wartezeitMs > 48 * 60 * 60_000;
  const isClaimed = !!item.reviewed_by;

  return (
    <TableRow
      hover={false}
      data-testid={selectable ? 'review-row-foreign' : 'review-row-own'}
      data-selectable={selectable ? 'true' : 'false'}
      sx={{
        cursor: 'default',
        bgcolor: rowBg,
        '&:hover': { bgcolor: rowBg },
      }}
    >
      <TableCell sx={selectable ? undefined : { borderLeft: `4px solid ${ownAccent}` }}>
        <Chip
          label={wartezeit}
          size="small"
          sx={{
            bgcolor: isUrgent ? alpha('#ef5350', 0.12) : alpha(accentColor, 0.12),
            color: isUrgent ? '#ef5350' : accentColor,
            fontWeight: 700,
            fontSize: '0.7rem',
            height: 22,
          }}
        />
      </TableCell>
      <TableCell>
        <Typography
          component={RouterLink}
          to={`/schwachstellen/${item.id}`}
          data-testid="review-title-link"
          sx={{
            fontWeight: 600,
            fontSize: '0.88rem',
            color: 'primary.main',
            textDecoration: 'none',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {item.titel}
        </Typography>
        {item.schwachstellenklasse && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {item.schwachstellenklasse.name_de}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
          {item.audit_name || '–'}
        </Typography>
      </TableCell>
      <TableCell>
        {item.kritikalitaet ? (
          <Chip
            label={formatSeverityLower(item.kritikalitaet)}
            size="small"
            color={getKritikalitaetMuiColor(item.kritikalitaet)}
          />
        ) : '–'}
      </TableCell>
      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        {item.cvss31_score != null ? (
          <Tooltip title={`CVSS 3.1: ${item.cvss31_score}`} arrow placement="top">
            <span>{item.cvss31_score}</span>
          </Tooltip>
        ) : '–'}
      </TableCell>
      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        {item.reporter
          ? `${item.reporter.vorname} ${item.reporter.nachname}`
          : '–'}
      </TableCell>
      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
        {isClaimed ? (
          <Chip
            label={`${item.reviewed_by!.vorname} ${item.reviewed_by!.nachname}`}
            size="small"
            sx={{
              bgcolor: alpha('#42a5f5', 0.12),
              color: '#42a5f5',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
            }}
          />
        ) : (
          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>–</Typography>
        )}
      </TableCell>
      <TableCell align="right">
        {selectable ? (
          <Button
            variant="contained"
            size="small"
            data-testid="review-action-claim"
            startIcon={<RateReviewIcon />}
            disabled={claiming}
            onClick={(e) => {
              e.stopPropagation();
              onClaim(item.id);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.78rem',
              whiteSpace: 'nowrap',
            }}
          >
            Review durchführen
          </Button>
        ) : (
          <Typography
            component="span"
            data-testid="review-action-unavailable"
            sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}
          >
            Nicht auswählbar
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

const ReviewQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();

  const [queue, setQueue] = useState<ReviewQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await schwachstellenService.reviewQueue();
        setQueue(data);
      } catch {
        // Global axios interceptor handles error display
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleClaimAndNavigate = async (id: number) => {
    try {
      setClaimingId(id);
      await schwachstellenService.claimReview(id);
      navigate(`/schwachstellen/${id}`);
    } catch {
      setClaimingId(null);
    }
  };

  const accentColor = isDark ? '#ffa726' : '#e65100';
  const emptyBg = isDark ? '#1a2e1a' : '#f1f8e9';
  const emptyColor = isDark ? '#66bb6a' : '#2e7d32';
  const ownAccent = isDark ? '#90a4ae' : '#546e7a';
  const ownSectionBg = alpha(ownAccent, isDark ? 0.22 : 0.12);
  const ownRowBg = alpha(ownAccent, isDark ? 0.1 : 0.06);

  const foreignReviews = queue.filter((item) => !isOwnReview(item, user?.id));
  const ownReviews = queue.filter((item) => isOwnReview(item, user?.id));

  if (loading) {
    return (
      <Box>
        <PageHeader title="Review-Warteschlange" />
        <TableSkeleton columns={7} rows={5} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Review-Warteschlange" />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
          mt: -1,
        }}
      >
        <Box
          data-testid="review-count-foreign"
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.75,
            px: 1.25,
            py: 0.4,
            borderRadius: 1,
            border: `1px solid ${alpha(accentColor, 0.35)}`,
            bgcolor: alpha(accentColor, 0.08),
          }}
        >
          <Typography
            component="span"
            sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2, color: accentColor }}
          >
            {foreignReviews.length}
          </Typography>
          <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.2 }}>
            in der Warteschlange, anwählbar
          </Typography>
        </Box>
        <Box
          data-testid="review-count-own"
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.75,
            px: 1.25,
            py: 0.4,
            borderRadius: 1,
            border: `1px solid ${alpha(ownAccent, 0.4)}`,
            bgcolor: alpha(ownAccent, 0.08),
          }}
        >
          <Typography
            component="span"
            sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2, color: ownAccent }}
          >
            {ownReviews.length}
          </Typography>
          <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.2 }}>
            eigene Reviews warten auf Prüfung
          </Typography>
        </Box>
      </Box>

      {queue.length === 0 ? (
        <Paper
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 3,
            bgcolor: emptyBg,
            border: `1px solid ${alpha(emptyColor, 0.3)}`,
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 48, color: emptyColor, mb: 1 }} />
          <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', color: emptyColor }}>
            Keine offenen Reviews – alles abgearbeitet!
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>Wartezeit</TableCell>
                <TableCell>Titel</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Audit</TableCell>
                <TableCell>Kritikalität</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>CVSS</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Reporter</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Beansprucht von</TableCell>
                <TableCell align="right">Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {foreignReviews.map((item) => (
                <ReviewQueueRow
                  key={item.id}
                  item={item}
                  selectable
                  accentColor={accentColor}
                  claiming={claimingId === item.id}
                  onClaim={handleClaimAndNavigate}
                />
              ))}
              {ownReviews.length > 0 && (
                <TableRow data-testid="review-section-eigene">
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    sx={{
                      bgcolor: ownSectionBg,
                      borderLeft: `4px solid ${ownAccent}`,
                      py: 1.25,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: ownAccent }}>
                      Eigene Reviews
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      Sichtbar, aber nicht auswählbar. Eigene Einreichungen können nicht selbst geprüft werden.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {ownReviews.map((item) => (
                <ReviewQueueRow
                  key={item.id}
                  item={item}
                  selectable={false}
                  accentColor={accentColor}
                  rowBg={ownRowBg}
                  ownAccent={ownAccent}
                  claiming={false}
                  onClaim={handleClaimAndNavigate}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ReviewQueuePage;
